import { useCallback, useMemo } from "react";
import { useSearchParams } from "react-router-dom";
import { useTripPublicationSummaryList } from "../../components/trips/hooks/useTripPublicationSummaryList";
import {
  DURATIONS,
  PAGE_SIZE,
  SORTS,
  getCountrySuggestions,
  getPriceBounds,
  matchesDateRange,
  matchesDestination,
  matchesDurations,
  matchesMaxPrice,
  sortItems,
} from "./searchResults";

/* Toutes les publications d'un coup : le filtrage se fait côté navigateur. */
const FETCH_SIZE = 100;

const positive = (value) => (Number(value) > 0 ? Number(value) : 0);

/**
 * État de la page résultats, entièrement porté par l'URL : recherche
 * (destination, dates, voyageurs), filtres (durée, budget), tri, affichage
 * et page. Un retour arrière ou un lien partagé restitue la même vue.
 */
export const useSearchResults = () => {
  const [searchParams, setSearchParams] = useSearchParams();

  const query = {
    destination: searchParams.get("destination") || "",
    startDate: searchParams.get("startDate") || "",
    endDate: searchParams.get("endDate") || "",
    adult: positive(searchParams.get("adult")),
    child: positive(searchParams.get("child")),
    baby: positive(searchParams.get("baby")),
  };

  const validDurations = DURATIONS.map((d) => d.value);
  const durations = (searchParams.get("duration") || "")
    .split(",")
    .filter((d) => validDurations.includes(d));
  const maxPrice = positive(searchParams.get("maxPrice"));
  const sort = SORTS.includes(searchParams.get("sort")) ? searchParams.get("sort") : "relevance";
  const view = searchParams.get("view") === "list" ? "list" : "grid";
  const requestedPage = Math.max(1, Math.floor(positive(searchParams.get("page"))) || 1);

  const { publications, loading, error } = useTripPublicationSummaryList({
    currentPage: 1,
    elementsPerPage: FETCH_SIZE,
    adultTravelers: query.adult || undefined,
    childTravelers: query.child || undefined,
    babiesTravelers: query.baby || undefined,
  });

  /* Correspondent à la recherche, avant les filtres de la colonne. */
  const searchMatches = useMemo(
    () =>
      publications.filter(
        (item) =>
          matchesDestination(item, query.destination) &&
          matchesDateRange(item, query.startDate, query.endDate)
      ),
    [publications, query.destination, query.startDate, query.endDate]
  );

  const priceBounds = useMemo(() => getPriceBounds(searchMatches), [searchMatches]);
  const durationKey = durations.join(",");

  const results = useMemo(
    () =>
      sortItems(
        searchMatches.filter(
          (item) => matchesDurations(item, durations) && matchesMaxPrice(item, maxPrice)
        ),
        sort
      ),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [searchMatches, durationKey, maxPrice, sort]
  );

  const totalPages = Math.max(1, Math.ceil(results.length / PAGE_SIZE));
  const page = Math.min(requestedPage, totalPages);
  const pageItems = results.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);

  const suggestions = useMemo(() => getCountrySuggestions(publications), [publications]);

  /* Met à jour l'URL. Tout changement de critère renvoie à la page 1. */
  const update = useCallback(
    (changes, { keepPage = false } = {}) => {
      setSearchParams(
        (prev) => {
          const next = new URLSearchParams(prev);
          Object.entries(changes).forEach(([key, value]) => {
            const empty =
              value === null ||
              value === undefined ||
              value === "" ||
              value === 0 ||
              (Array.isArray(value) && value.length === 0);
            if (empty) next.delete(key);
            else next.set(key, Array.isArray(value) ? value.join(",") : String(value));
          });
          if (!keepPage) next.delete("page");
          if (next.get("sort") === "relevance") next.delete("sort");
          if (next.get("view") === "grid") next.delete("view");
          if (next.get("page") === "1") next.delete("page");
          return next;
        },
        { replace: !("page" in changes) }
      );
    },
    [setSearchParams]
  );

  return {
    query,
    durations,
    maxPrice,
    sort,
    view,
    page,
    totalPages,
    loading: loading && publications.length === 0,
    error,
    total: results.length,
    pageItems,
    priceBounds,
    suggestions,
    update,
  };
};
